import {
  catchError,
  concatMap,
  exhaustMap,
  map,
  mergeMap,
  withLatestFrom
} from 'rxjs/operators'
import { AppState } from 'src/app/core/core.state'
import { normalizeToSchema } from 'src/app/shared/normalization/normalization'
import { normalizationActions } from 'src/app/shared/normalization/normalization.actions'
import { ResourceEffects } from 'src/app/shared/resource-effects'

import { Injectable } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'

import { Booking } from './booking'
import { bookingActions } from './booking.actions'
import * as fromBooking from './booking.reducer'
import { BookingService } from './booking.service'
import moment from 'moment-mini'
import { bookingSelectors } from './booking.selectors'
import { of } from 'rxjs'
import { participationActions } from './participation/participation.actions'
import { employeeBookingActions } from '../employee-booking/employee-booking.actions'

@Injectable()
export class BookingEffects extends ResourceEffects<Booking> {
  constructor(
    public actions$: Actions,
    public store: Store<AppState>,
    public bookingService: BookingService
  ) {
    super(fromBooking.FEATURE_NAME, bookingService, bookingActions, actions$)
  }

  fetchManyPublic = createEffect(() =>
    this.actions$.pipe(
      ofType(bookingActions.fetchManyPublic),
      exhaustMap((action) =>
        this.bookingService.getManyPublic(action.params).pipe(
          map((bookings) =>
            normalizationActions.upsertMany({
              entities: normalizeToSchema({ bookings })
            })
          )
        )
      )
    )
  )
  fetchBookingsNeedsSupervisor = createEffect(() =>
    this.actions$.pipe(
      ofType(bookingActions.fetchBookingsNeedsSupervisor),
      exhaustMap((action: any) =>
        this.bookingService
          .getBookingsWithSupervisorNeeds(
            action.page,
            action.pageSize,
            action.startDate,
            action.endDate,
            action.order
          )
          .pipe(
            map((response: any) => {
              this.store.dispatch(
                bookingActions.actionFinished({
                  actionFinished: {
                    action: bookingActions.fetchBookingsNeedsSupervisor.type,
                    data: response
                  }
                })
              )

              return normalizationActions.upsertMany({
                entities: normalizeToSchema({
                  bookings: response.data ?? response
                })
              })
            }),
            catchError((error) =>
              of(bookingActions.fetchBookingsNeedsSupervisorFailure({ error }))
            )
          )
      )
    )
  )
  createOneWithOneMinuteLess = createEffect(() =>
    this.actions$.pipe(
      ofType(bookingActions.createOneWithOneMinuteLess),

      concatMap((action) => {
        let bookingWithLessMinute: Booking = JSON.parse(
          JSON.stringify(action.entity)
        )
        if (bookingWithLessMinute.endDate) {
          bookingWithLessMinute.endDate = moment(bookingWithLessMinute.endDate)
            .subtract(1, 'minute')
            .toISOString()
        }

        return this.bookingService.createOne(action.entity).pipe(
          map((booking) => {
            return bookingActions.upsertOne({
              entity: {
                ...bookingWithLessMinute
              }
            })
          }),
          catchError((error) => {
            this.store.dispatch(bookingActions.setLoading({ isLoading: false }))
            return of(
              bookingActions.createOneFailed({
                entity: bookingWithLessMinute
              })
            )
          })
        )
      })
    )
  )
  updateOneById = createEffect(() =>
    this.actions$.pipe(
      ofType(bookingActions.updateOneById),
      withLatestFrom(this.store.select(bookingSelectors.selectEntities)),
      concatMap(([action, bookings]) => {
        if (action.id && bookings[action.id]) {
          const booking = bookings[action.id]

          let bookingWithLessMinute: Booking = JSON.parse(
            JSON.stringify(action.params)
          )
          if (!moment(booking.endDate).isSame(bookingWithLessMinute.endDate)) {
            bookingWithLessMinute.endDate = moment(
              bookingWithLessMinute.endDate
            )
              .subtract(1, 'minute')
              .toISOString()
          }

          return this.bookingService
            .updateOne(action.id, bookingWithLessMinute)
            .pipe(
              map((booking) => {
                this.store.dispatch(
                  bookingActions.actionFinished({
                    actionFinished: {
                      action: bookingActions.updateOneById.type,
                      data: booking
                    }
                  })
                )
                return bookingActions.upsertOne({
                  entity: {
                    id: action.id,
                    ...bookingWithLessMinute
                  }
                })
              })
            )
        }
      })
    )
  )

  createManyWithOneMinuteLess = createEffect(() =>
    this.actions$.pipe(
      ofType(bookingActions.createManyWithOneMinuteLess),
      concatMap((action) => {
        let bookingsWithLessMinute: Booking[] = JSON.parse(
          JSON.stringify(action?.entities)
        )
        bookingsWithLessMinute?.map((booking) => {
          if (booking.endDate) {
            booking.endDate = moment(booking.endDate)
              .subtract(1, 'minute')
              .toISOString()
          }
        })

        return this.bookingService.createMany(bookingsWithLessMinute).pipe(
          mergeMap((response) => {
            const bookings: Booking[] = Object.values(response).filter(
              (e: any) => e?.id
            )
            return [
              bookingActions.actionFinished({
                actionFinished: {
                  action: bookingActions.createManyWithOneMinuteLess.type,
                  data: bookings
                }
              }),
              bookingActions.createManySuccess({ entities: bookings }),
              normalizationActions.upsertMany({
                entities: normalizeToSchema({ bookings })
              })
            ]
          }),
          catchError(() => of(bookingActions.createManyFailed()))
        )
      })
    )
  )
  fetchManySpecial = createEffect(() =>
    this.actions$.pipe(
      ofType(bookingActions.specialFetchMany),
      concatMap((action) =>
        this.bookingService.getManySpecial(action.params).pipe(
          map((bookings: any) =>
            normalizationActions.upsertMany({
              entities: normalizeToSchema({ bookings: bookings.data }),
              total: bookings.total,
              pageCount: bookings.pageCount
            })
          )
        )
      )
    )
  )
  getAllMany = createEffect(() =>
    this.actions$.pipe(
      ofType(bookingActions.getAllMany),
      concatMap((action) =>
        this.bookingService.getAllMany(action.params).pipe(
          map((bookings: any) => {
            this.store.dispatch(
              bookingActions.actionFinished({
                actionFinished: {
                  action: bookingActions.getAllMany.type,
                  data: bookings
                }
              })
            )

            return normalizationActions.upsertMany({
              entities: normalizeToSchema({
                bookings: bookings.data ?? bookings
              })
            })
          })
        )
      )
    )
  )
  getAllEmployeeRelations = createEffect(() =>
    this.actions$.pipe(
      ofType(bookingActions.getAllEmployeeRelations),
      concatMap((action) =>
        this.bookingService
          .getAllEmployeeRelations(action.data, action.params)
          .pipe(
            map((response: any) => {
              return bookingActions.actionFinished({
                actionFinished: {
                  action: bookingActions.getAllEmployeeRelations.type,
                  data: response
                }
              })
            })
          )
      )
    )
  )

  fetchManyByMemberId = createEffect(() =>
    this.actions$.pipe(
      ofType(bookingActions.fetchManyByMemberId),
      exhaustMap((action) =>
        this.bookingService
          .getManyByMemberId(action.memberId, action.params)
          .pipe(
            map((bookings) =>
              normalizationActions.upsertMany({
                entities: normalizeToSchema({ bookings })
              })
            )
          )
      )
    )
  )

  changeBookingParticipantsByCategory = createEffect(() =>
    this.actions$.pipe(
      ofType(bookingActions.changeBookingParticipantsByCategory),
      exhaustMap((action) =>
        this.bookingService
          .changeBookingParticipantsByCategory(action.bookingId, action.params)
          .pipe(
            mergeMap((participationAdjustmentResult) => {
              const actions = []

              actions.push(
                bookingActions.changeBookingParticipantsByCategorySuccess({
                  booking: participationAdjustmentResult.booking
                })
              )

              if (
                participationAdjustmentResult.deletedParticipantIds.length > 0
              ) {
                actions.push(
                  participationActions.removeMany({
                    ids: participationAdjustmentResult.deletedParticipantIds
                  })
                )
              }
              return actions
            }),
            catchError((error) => {
              const errorMessage =
                error?.error?.message || error?.message || 'alerts.error'
              return of(
                bookingActions.changeBookingParticipantsByCategoryFailed({
                  error: errorMessage
                })
              )
            })
          )
      )
    )
  )

  changeBookingParticipantsByCategorySuccess = createEffect(() =>
    this.actions$.pipe(
      ofType(bookingActions.changeBookingParticipantsByCategorySuccess),
      map((action) =>
        normalizationActions.upsertMany({
          entities: normalizeToSchema({ bookings: [action.booking] })
        })
      )
    )
  )

  deleteMany = createEffect(() =>
    this.actions$.pipe(
      ofType(bookingActions.deleteMany),
      concatMap((action) =>
        this.bookingService
          .deleteMany(
            action.ids,
            action.refundPayMethod,
            action.sendCancelationEmail,
            action.deleteRelatedEmployeeBookings,
            action.cancelationPolicyUuid
          )
          .pipe(
            mergeMap((result) => {
              const actions = []
              if (result.deletedParticipationIds.length) {
                actions.push(
                  bookingActions.removeMany({
                    ids: result.deletedParticipationIds
                  })
                )
              }
              if (result.deletedBookingIds.length) {
                actions.push(
                  bookingActions.removeMany({
                    ids: result.deletedBookingIds
                  })
                )
              }

              if (result.deletedEmployeeBookingIds.length) {
                actions.push(
                  employeeBookingActions.removeMany({
                    ids: result.deletedEmployeeBookingIds
                  })
                )
              }

              actions.push(bookingActions.deleteManySuccess({ ...result }))

              return actions
            }),
            catchError(() => {
              return of(this.resourceActions.deleteManyFailed({}))
            })
          )
      )
    )
  )
}
